/**
 * Multi-selection for the desktop Mindmap, Bubbles and Gantt views and the
 * task lists (Gantt task list, Structure sidebar).
 *
 * Build a selection with Shift+drag on a view (rectangle: Mindmap and Bubbles
 * take the nodes fully inside it, Gantt the bars it touches), Ctrl/Cmd+click
 * on nodes (add / remove), or Shift+click in a list (range). While nodes are
 * selected the app is in selection mode: selected nodes carry an animated
 * halo, a bar shows the count, T (or the bar's Tags button) tags them all
 * with a tri-state popup, and dragging a selected node onto a target (Ctrl+drag
 * in Mindmap and Bubbles, a list drag in Gantt and the sidebar) moves them
 * all. Other shortcuts are off in those three views (other views keep theirs,
 * and ? help always works). Only Esc, the bar's ✕ or a click elsewhere leaves
 * the mode — a click, not a drag, so panning keeps the selection; switching
 * views or themes keeps it too (clicks on the header, tabs, breadcrumbs and
 * view controls don't count as "elsewhere"). Esc also cancels a rectangle
 * being drawn. The focused node (the view's root) is never selected: moving
 * the selection under it would be refused. Off in the mobile layout.
 *
 * The selection is a set of node ids, applied to whatever the current view
 * renders (a MutationObserver re-applies the halo after re-renders).
 */
import { escapeHtml } from "../../../engine/core/validation.js";
import { t } from "../../../shared/i18n/index.js";

// Where a rectangle can start, and which elements it can catch, per view.
const VIEWS = {
  mindmap: { area: "#m-canvas", nodes: ".mm-node[data-id]:not(.root)", catches: "inside" },
  bubble: { area: "#b-canvas", nodes: ".bubble[data-id]", catches: "inside" },
  gantt: { area: "#g-body", nodes: ".gantt-bar[data-id]:not(.not-editable)", catches: "touch" },
};
// Elements that stand for a node, in views and lists.
const NODE_ELEMENTS = [
  ".mm-node[data-id]:not(.root)",
  ".bubble[data-id]",
  ".gantt-bar[data-id]:not(.not-editable)",
  ".gantt-label-row[data-id]",
  "#tree .tree-node[data-id]",
].join(", ");
// Lists where Shift+click selects a range, in display order. Gantt draws only
// the rows near the screen: its order comes from the view (`order`).
const LISTS = [
  { list: "#g-labels", rows: ".gantt-label-row[data-id]", order: () => callbacks.ganttRowIds?.() },
  { list: "#tree", rows: ".tree-node[data-id]" },
];
// Clicks here don't leave the mode: the bar, popups, the top header (theme,
// undo / redo, save…), and navigation (view tabs, breadcrumbs, view
// controls, sidebar toggle and resize).
const KEEP_MODE = [
  ".multi-select-bar", ".tag-popup", ".modal", ".toast", ".context-menu",
  ".header", ".theme-dropdown",
  ".tab", ".breadcrumb", ".controls", ".sidebar-toggle", ".sidebar-resize-handle",
].join(", ");

let callbacks = null;
const ids = new Set();
let anchorId = null; // last node clicked in a list, for Shift+click ranges
let barEl = null;
let observer = null;
let applyScheduled = false;
let rect = null; // rectangle being drawn
let pointerDown = null; // {x, y} of the last left mousedown
let suppressClick = false; // the click that ends a rectangle
let pendingClear = false; // a press elsewhere: leave the mode if it ends as a click
let lastMouse = { x: 0, y: 0 };

/**
 * @param {Object} cbs
 * @param {() => Object} cbs.getState - Store state (currentView, selectedId, …)
 * @param {() => boolean} cbs.isReadOnly
 * @param {(ids: string[], parentId: string, index: number) => {moved: number, refused?: string}} cbs.moveNodes
 * @param {(ids: string[], x: number, y: number) => void} cbs.openTags
 * @param {() => boolean} cbs.isTagPopupOpen
 * @param {() => boolean} cbs.isModalOpen - a modal owns the keyboard
 * @param {() => boolean} [cbs.isEnabled] - false in the mobile layout
 * @param {(message: string) => void} cbs.notify
 */
export function init(cbs) {
  callbacks = cbs;
  window.addEventListener("mousemove", (e) => { lastMouse = { x: e.clientX, y: e.clientY }; }, true);
  window.addEventListener("keydown", onKeydown, true);
  window.addEventListener("mousedown", onMousedown, true);
  window.addEventListener("click", onClick, true);
}

/** Whether selection mode is on (at least one node selected). */
export function isActive() {
  return ids.size > 0;
}

function enabled() {
  return !!callbacks && callbacks.isEnabled?.() !== false;
}

// The focused node is the view's root: never part of the selection.
function selectable(id) {
  return !!id && id !== callbacks?.getState?.()?.focusId;
}

export function has(id) {
  return ids.has(id);
}

export function getIds() {
  return [...ids];
}

export function clear() {
  if (!ids.size && !rect) return;
  cancelRect();
  ids.clear();
  anchorId = null;
  update();
}

/** Re-apply the display (after a view switch: halo, bar hint). */
export function refresh() {
  if (ids.size) update();
}

/** Replace the selection. */
export function set(nodeIds) {
  ids.clear();
  for (const id of nodeIds) if (selectable(id)) ids.add(id);
  update();
}

/**
 * Move the whole selection under `parentId` (drag and drop of a selected
 * node, see movesSelection). Nodes inside another selected node move with
 * it; a move into one of the selected nodes is refused, with a message, and
 * nodes that couldn't move (another map, read-only) are reported.
 * @returns {{moved: number, movedIds: string[], attempted: number, refused?: string}}
 */
export function moveSelectionTo(parentId, index = -1) {
  if (!callbacks) return { moved: 0, movedIds: [] };
  const result = callbacks.moveNodes(getIds(), parentId, index) || { moved: 0, movedIds: [] };
  if (result.refused === "inside") {
    callbacks.notify?.(t("app.multiSelect.moveInside"));
  } else {
    const stayed = (result.attempted || 0) - (result.moved || 0);
    if (stayed > 0) {
      callbacks.notify?.(t("app.multiSelect.notMoved", { count: stayed }));
    }
  }
  return result;
}

/**
 * Selected nodes that are not inside another selected node (the ones a move
 * carries), given a findParent lookup.
 */
export function topLevelIds(findParent) {
  return getIds().filter((id) => {
    for (let p = findParent(id); p; p = findParent(p.id)) if (ids.has(p.id)) return false;
    return true;
  });
}

/** Whether a drag of this node should move the whole selection. */
export function movesSelection(nodeId) {
  return ids.size > 1 && ids.has(nodeId);
}

// ---------------------------------------------------------------------------
// Display: halo on selected elements, the bar, body class
// ---------------------------------------------------------------------------

// Above this many selected nodes the halo doesn't breathe (a GPU layer each).
const MAX_ANIMATED = 40;

function update() {
  document.body.classList.toggle("multi-select-mode", ids.size > 0);
  document.body.classList.toggle("multi-select-many", ids.size > MAX_ANIMATED);
  renderBar();
  applyClasses();
  if (ids.size && !observer) {
    observer = new MutationObserver(scheduleApply);
    observer.observe(document.body, { childList: true, subtree: true });
  } else if (!ids.size && observer) {
    observer.disconnect();
    observer = null;
  }
}

function scheduleApply() {
  if (applyScheduled) return;
  applyScheduled = true;
  requestAnimationFrame(() => {
    applyScheduled = false;
    applyClasses();
  });
}

// One pass over the node elements (not one query per selected id).
function applyClasses() {
  for (const el of document.querySelectorAll(".multi-selected")) {
    if (!ids.has(el.dataset.id)) el.classList.remove("multi-selected");
  }
  if (!ids.size) return;
  for (const el of document.querySelectorAll(NODE_ELEMENTS)) {
    if (ids.has(el.dataset.id)) el.classList.add("multi-selected");
  }
}

function renderBar() {
  if (!ids.size) {
    barEl?.remove();
    barEl = null;
    return;
  }
  if (!barEl) {
    barEl = document.createElement("div");
    barEl.className = "multi-select-bar";
    barEl.setAttribute("role", "toolbar");
    barEl.setAttribute("aria-label", t("app.multiSelect.label"));
    barEl.addEventListener("click", (e) => {
      const action = e.target.closest("[data-ms-action]")?.dataset.msAction;
      if (action === "tags") openTags(e.clientX, e.clientY - 260);
      else if (action === "clear") clear();
    });
    document.body.appendChild(barEl);
  }
  const view = callbacks?.getState?.()?.currentView;
  const moveHint = view === "mindmap" || view === "bubble"
    ? t("app.multiSelect.hintMap")
    : view === "gantt" ? t("app.multiSelect.hintGantt") : "";
  const leave = escapeHtml(t("app.multiSelect.leave"));
  barEl.innerHTML = `
    <span class="multi-select-count">${escapeHtml(t("app.multiSelect.count", { count: ids.size }))}</span>
    <button type="button" class="multi-select-btn" data-ms-action="tags" title="${escapeHtml(t("app.multiSelect.tagAll"))} (T)">${escapeHtml(t("common.tags"))} <kbd>T</kbd></button>
    ${moveHint ? `<span class="multi-select-hint">${escapeHtml(moveHint)}</span>` : ""}
    <button type="button" class="multi-select-close" data-ms-action="clear" title="${leave} (Esc)" aria-label="${leave}">✕</button>`;
}

function openTags(x, y) {
  if (!ids.size || callbacks?.isReadOnly?.()) return;
  callbacks.openTags(getIds(), Math.max(8, x), Math.max(8, y));
}

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

function isTyping(target) {
  return target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.tagName === "SELECT" || target?.isContentEditable;
}

function isContextMenuOpen() {
  const menu = document.querySelector(".context-menu");
  return !!menu && getComputedStyle(menu).display !== "none";
}

function onKeydown(e) {
  if (!enabled()) return;
  if (rect && e.key === "Escape") {
    e.preventDefault();
    e.stopImmediatePropagation();
    cancelRect();
    return;
  }
  // Text fields and open modals keep all their keys (Esc closes the modal).
  if (!ids.size || isTyping(e.target) || callbacks?.isModalOpen?.()) return;
  if (e.key === "Escape") {
    // Esc first closes the tag popup or a context menu, or cancels a drag.
    if (callbacks?.isTagPopupOpen?.() || isContextMenuOpen() ||
        document.body.classList.contains("dragging-node")) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    clear();
    return;
  }
  // Views that don't show the selection keep all their shortcuts.
  if (!VIEWS[currentView()]) return;
  const mod = e.ctrlKey || e.metaKey;
  if ((e.key === "t" || e.key === "T") && !mod && !e.altKey) {
    e.preventDefault();
    e.stopImmediatePropagation();
    openTags(lastMouse.x, lastMouse.y);
    return;
  }
  // Undo / redo stay available; every other app shortcut is off in this mode
  // (browser shortcuts keep working: nothing is prevented).
  if (mod && ["z", "Z", "y", "Y"].includes(e.key)) return;
  if (e.key === "?" || ["Shift", "Control", "Meta", "Alt"].includes(e.key)) return;
  e.stopImmediatePropagation();
}

function currentView() {
  return callbacks?.getState?.()?.currentView;
}

function onMousedown(e) {
  if (e.button !== 0 || !enabled() || isTyping(e.target)) return;
  pointerDown = { x: e.clientX, y: e.clientY };
  pendingClear = false;
  const view = VIEWS[currentView()];

  // Shift+drag on a view draws a selection rectangle (even over nodes: plain
  // drag moves them and Ctrl+drag reparents, Shift+drag had no meaning).
  const area = view && e.shiftKey && !e.ctrlKey && !e.metaKey && e.target.closest(view.area);
  if (area && !callbacks.isReadOnly?.()) {
    e.preventDefault();
    e.stopImmediatePropagation();
    startRect(e, area, view);
    return;
  }

  if (!ids.size) return;
  // A click elsewhere leaves the mode (decided on click: a drag or pan keeps
  // it). Ctrl/Shift clicks, the bar, popups and navigation keep it, and so
  // does a press on a selected node (dragging the selection).
  if (e.ctrlKey || e.metaKey || e.shiftKey || e.target.closest(KEEP_MODE)) return;
  const nodeEl = e.target.closest(NODE_ELEMENTS);
  if (nodeEl && ids.has(nodeEl.dataset.id)) return;
  pendingClear = true;
}

function onClick(e) {
  if (suppressClick) {
    suppressClick = false;
    e.preventDefault();
    e.stopImmediatePropagation();
    return;
  }
  if (e.button !== 0 || !enabled()) return;
  const moved = pointerDown ? Math.hypot(e.clientX - pointerDown.x, e.clientY - pointerDown.y) : 0;
  if (pendingClear) {
    pendingClear = false;
    if (moved <= 4) clear(); // then the click does what it normally does
    return;
  }
  if (callbacks.isReadOnly?.() || isTyping(e.target)) return;
  if (moved > 4) return; // the end of a drag, not a click
  const nodeEl = e.target.closest(NODE_ELEMENTS);
  if (!nodeEl) return;
  const id = nodeEl.dataset.id;

  if (e.shiftKey && !e.ctrlKey && !e.metaKey) {
    const list = LISTS.find((l) => nodeEl.matches(`${l.list} ${l.rows}`));
    if (!list) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    selectRange(list, id);
    return;
  }
  if (!(e.ctrlKey || e.metaKey)) {
    if (LISTS.some((l) => nodeEl.matches(`${l.list} ${l.rows}`))) anchorId = id;
    return;
  }
  e.preventDefault();
  e.stopImmediatePropagation();
  if (!selectable(id)) return;
  // Starting a selection keeps the node selected so far, when it is shown.
  if (!ids.size) {
    const selectedId = callbacks.getState?.()?.selectedId;
    if (selectedId && selectedId !== id && selectable(selectedId) &&
        [...document.querySelectorAll(NODE_ELEMENTS)].some((el) => el.dataset.id === selectedId)) {
      ids.add(selectedId);
    }
  }
  if (ids.has(id)) ids.delete(id);
  else ids.add(id);
  anchorId = id;
  update();
}

function selectRange(list, id) {
  const container = document.querySelector(list.list);
  const rowIds = list.order?.() || [...(container?.querySelectorAll(list.rows) || [])].map((r) => r.dataset.id);
  const from = rowIds.indexOf(anchorId ?? callbacks.getState?.()?.selectedId);
  const to = rowIds.indexOf(id);
  if (to < 0) return;
  const [a, b] = from < 0 ? [to, to] : [Math.min(from, to), Math.max(from, to)];
  for (const rowId of rowIds.slice(a, b + 1)) if (selectable(rowId)) ids.add(rowId);
  anchorId = id;
  update();
}

// ---------------------------------------------------------------------------
// Rectangle
// ---------------------------------------------------------------------------

function startRect(e, area, view) {
  const el = document.createElement("div");
  el.className = "multi-select-rect";
  document.body.appendChild(el);
  rect = { el, area, view, x0: e.clientX, y0: e.clientY, x1: e.clientX, y1: e.clientY };
  drawRect();
  window.addEventListener("mousemove", onRectMove, true);
  window.addEventListener("mouseup", onRectUp, true);
}

function drawRect() {
  const { el, x0, y0, x1, y1 } = rect;
  el.style.left = `${Math.min(x0, x1)}px`;
  el.style.top = `${Math.min(y0, y1)}px`;
  el.style.width = `${Math.abs(x1 - x0)}px`;
  el.style.height = `${Math.abs(y1 - y0)}px`;
}

function onRectMove(e) {
  if (!rect) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  rect.x1 = e.clientX;
  rect.y1 = e.clientY;
  drawRect();
}

function onRectUp(e) {
  if (!rect) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  const { area, view, x0, y0, x1, y1 } = rect;
  cancelRect();
  // The browser follows this mouseup with a click; don't let it select a node.
  suppressClick = true;
  setTimeout(() => { suppressClick = false; }, 0);
  const left = Math.min(x0, x1), right = Math.max(x0, x1);
  const top = Math.min(y0, y1), bottom = Math.max(y0, y1);
  if (right - left < 4 && bottom - top < 4) return;
  let added = 0;
  for (const el of area.querySelectorAll(view.nodes)) {
    const r = el.getBoundingClientRect();
    if (!r.width && !r.height) continue;
    const hit = view.catches === "inside"
      ? r.left >= left && r.right <= right && r.top >= top && r.bottom <= bottom
      : r.left < right && r.right > left && r.top < bottom && r.bottom > top;
    if (hit && selectable(el.dataset.id)) {
      ids.add(el.dataset.id);
      added++;
    }
  }
  if (added) update();
}

function cancelRect() {
  if (!rect) return;
  rect.el.remove();
  rect = null;
  window.removeEventListener("mousemove", onRectMove, true);
  window.removeEventListener("mouseup", onRectUp, true);
}
